/**
 * Task Aging Dashboard Component
 * Displays aging tasks with warning, critical, and stale status
 */
import React, { useState, useEffect } from 'react';
import { agingAPI } from '../services/api';
import '../styles/TaskAgingDashboard.css';

const TaskAgingDashboard = ({ projectId = null }) => {
  const [agingSummary, setAgingSummary] = useState(null);
  const [agedTasks, setAgedTasks] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configEditing, setConfigEditing] = useState(false);
  const [tempConfig, setTempConfig] = useState(null);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAgingSummary(),
        fetchConfig()
      ]);
    } catch (error) {
      console.error('Error fetching aging data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgingSummary = async () => {
    try {
      const response = await agingAPI.getAgingSummary(projectId);
      setAgingSummary(response.data);
      setAgedTasks(response.data.aged_tasks);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await agingAPI.getConfig();
      setConfig(response.data);
      setTempConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const filterByLevel = async (level) => {
    setSelectedLevel(level);
    try {
      const response = await agingAPI.getAgedTasks({ level, project_id: projectId });
      setAgedTasks(response.data);
    } catch (error) {
      console.error('Error filtering tasks:', error);
    }
  };

  const handleConfigUpdate = async () => {
    try {
      await agingAPI.updateConfig({
        warning_days: tempConfig.warning_days,
        critical_days: tempConfig.critical_days,
        stale_days: tempConfig.stale_days,
        include_weekends: tempConfig.include_weekends
      });
      setConfig(tempConfig);
      setConfigEditing(false);
      await fetchAgingSummary(); // Refresh data with new config
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Ayarlar güncellenemedi!');
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      normal: '#10b981',
      warning: '#f59e0b',
      critical: '#ef4444',
      stale: '#6b7280'
    };
    return colors[level?.toLowerCase()] || '#6b7280';
  };

  const getLevelIcon = (level) => {
    const icons = {
      normal: '✅',
      warning: '⚠️',
      critical: '🔴',
      stale: '💀'
    };
    return icons[level?.toLowerCase()] || '⏰';
  };

  const getLevelText = (level) => {
    const texts = {
      normal: 'Normal',
      warning: 'Uyarı',
      critical: 'Kritik',
      stale: 'Bayat'
    };
    return texts[level?.toLowerCase()] || level;
  };

  if (loading) {
    return (
      <div className="aging-dashboard loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!agingSummary) {
    return (
      <div className="aging-dashboard error">
        <p>Veri yüklenemedi</p>
        <button onClick={fetchData}>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="aging-dashboard">
      <div className="dashboard-header">
        <h2>
          <span className="icon">⏰</span>
          Görev Yaşlandırma Takibi
        </h2>
        <button className="refresh-btn" onClick={fetchData}>
          🔄 Yenile
        </button>
      </div>

      {/* Summary Cards */}
      <div className="aging-summary">
        <div className="summary-card total">
          <span className="card-count">{agingSummary.total_pending}</span>
          <span className="card-label">Toplam Bekleyen</span>
        </div>

        <div
          className="summary-card warning clickable"
          onClick={() => filterByLevel('warning')}
        >
          <span className="card-count">{agingSummary.warning_count}</span>
          <span className="card-label">⚠️ Uyarı</span>
        </div>

        <div
          className="summary-card critical clickable"
          onClick={() => filterByLevel('critical')}
        >
          <span className="card-count">{agingSummary.critical_count}</span>
          <span className="card-label">🔴 Kritik</span>
        </div>

        <div
          className="summary-card stale clickable"
          onClick={() => filterByLevel('stale')}
        >
          <span className="card-count">{agingSummary.stale_count}</span>
          <span className="card-label">💀 Bayat</span>
        </div>
      </div>

      {/* Configuration */}
      {config && (
        <div className="aging-config">
          <div className="config-header">
            <h3>⚙️ Yaşlandırma Ayarları</h3>
            {!configEditing ? (
              <button onClick={() => setConfigEditing(true)}>
                ✏️ Düzenle
              </button>
            ) : (
              <div className="config-actions">
                <button className="save-btn" onClick={handleConfigUpdate}>
                  💾 Kaydet
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setTempConfig(config);
                    setConfigEditing(false);
                  }}
                >
                  ❌ İptal
                </button>
              </div>
            )}
          </div>

          <div className="config-grid">
            <div className="config-item">
              <label>⚠️ Uyarı (gün):</label>
              <input
                type="number"
                value={tempConfig.warning_days}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  warning_days: parseInt(e.target.value)
                })}
                disabled={!configEditing}
                min="1"
              />
            </div>

            <div className="config-item">
              <label>🔴 Kritik (gün):</label>
              <input
                type="number"
                value={tempConfig.critical_days}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  critical_days: parseInt(e.target.value)
                })}
                disabled={!configEditing}
                min="1"
              />
            </div>

            <div className="config-item">
              <label>💀 Bayat (gün):</label>
              <input
                type="number"
                value={tempConfig.stale_days}
                onChange={(e) => setTempConfig({
                  ...tempConfig,
                  stale_days: parseInt(e.target.value)
                })}
                disabled={!configEditing}
                min="1"
              />
            </div>

            <div className="config-item checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={tempConfig.include_weekends}
                  onChange={(e) => setTempConfig({
                    ...tempConfig,
                    include_weekends: e.target.checked
                  })}
                  disabled={!configEditing}
                />
                Hafta sonlarını dahil et
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <button
          className={!selectedLevel ? 'active' : ''}
          onClick={() => filterByLevel(null)}
        >
          Tümü
        </button>
        <button
          className={selectedLevel === 'warning' ? 'active warning' : 'warning'}
          onClick={() => filterByLevel('warning')}
        >
          ⚠️ Uyarı
        </button>
        <button
          className={selectedLevel === 'critical' ? 'active critical' : 'critical'}
          onClick={() => filterByLevel('critical')}
        >
          🔴 Kritik
        </button>
        <button
          className={selectedLevel === 'stale' ? 'active stale' : 'stale'}
          onClick={() => filterByLevel('stale')}
        >
          💀 Bayat
        </button>
      </div>

      {/* Tasks List */}
      <div className="aged-tasks-section">
        <h3>
          Yaşlanan Görevler
          {selectedLevel && ` - ${getLevelText(selectedLevel)}`}
        </h3>

        {agedTasks.length === 0 ? (
          <div className="no-tasks">
            <span className="celebration">🎉</span>
            <p>Yaşlanan görev yok!</p>
          </div>
        ) : (
          <div className="tasks-table-container">
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>Seviye</th>
                  <th>Görev</th>
                  <th>Proje</th>
                  <th>Atanan</th>
                  <th>Bekleyen</th>
                  <th>Son Aktivite</th>
                </tr>
              </thead>
              <tbody>
                {agedTasks.map(task => (
                  <tr key={task.id} className={`task-row ${task.aging_level}`}>
                    <td>
                      <span
                        className="level-badge"
                        style={{ backgroundColor: getLevelColor(task.aging_level) }}
                      >
                        {getLevelIcon(task.aging_level)} {getLevelText(task.aging_level)}
                      </span>
                    </td>
                    <td className="task-title">{task.title}</td>
                    <td className="project-name">{task.project_name}</td>
                    <td className="assignee">{task.assignee_name || 'Atanmamış'}</td>
                    <td className="days-pending">
                      <strong>{task.days_pending}</strong> gün
                    </td>
                    <td className="last-activity">
                      {new Date(task.last_activity).toLocaleDateString('tr-TR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Distribution by Project */}
      {Object.keys(agingSummary.by_project || {}).length > 0 && (
        <div className="distribution-section">
          <h3>📊 Proje Bazında Dağılım</h3>
          <div className="distribution-grid">
            {Object.entries(agingSummary.by_project).map(([project, counts]) => (
              <div key={project} className="distribution-card">
                <h4>{project}</h4>
                <div className="distribution-stats">
                  {counts.warning > 0 && (
                    <span className="stat warning">⚠️ {counts.warning}</span>
                  )}
                  {counts.critical > 0 && (
                    <span className="stat critical">🔴 {counts.critical}</span>
                  )}
                  {counts.stale > 0 && (
                    <span className="stat stale">💀 {counts.stale}</span>
                  )}
                  {counts.warning === 0 && counts.critical === 0 && counts.stale === 0 && (
                    <span className="stat normal">✅ Sağlıklı</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distribution by Assignee */}
      {Object.keys(agingSummary.by_assignee || {}).length > 0 && (
        <div className="distribution-section">
          <h3>👥 Kişi Bazında Dağılım</h3>
          <div className="distribution-grid">
            {Object.entries(agingSummary.by_assignee).map(([assignee, counts]) => (
              <div key={assignee} className="distribution-card">
                <h4>{assignee}</h4>
                <div className="distribution-stats">
                  {counts.warning > 0 && (
                    <span className="stat warning">⚠️ {counts.warning}</span>
                  )}
                  {counts.critical > 0 && (
                    <span className="stat critical">🔴 {counts.critical}</span>
                  )}
                  {counts.stale > 0 && (
                    <span className="stat stale">💀 {counts.stale}</span>
                  )}
                  {counts.warning === 0 && counts.critical === 0 && counts.stale === 0 && (
                    <span className="stat normal">✅ Sağlıklı</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskAgingDashboard;
